import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Shield, 
  Scale, 
  Server, 
  Lock,
  FileCheck,
  Building2,
  Stethoscope,
  Plane,
  Gavel,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Globe,
  Cpu,
  Eye,
  Users,
  FileText,
  Briefcase,
  MapPin
} from "lucide-react";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import { useContactForm } from "@/components/ContactFormModal";
import { ServicePageJsonLd } from "@/components/seo/PageJsonLd";

const regulationIcons = [Scale, FileCheck, Eye, Clock];
const infrastructureIcons = [Server, Cpu, Lock, Globe];
const sectorIcons = [Plane, Stethoscope, Gavel];
const liabilityIcons = [Briefcase, Users, FileText];
const advantageIcons = [MapPin, Building2, Shield];

export default function SovereignAI() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const { openContactForm } = useContactForm();

  const regulations = (t("sovereignAI.regulations.items", { returnObjects: true }) as Array<{ title: string; description: string }>);
  const infrastructureFeatures = (t("sovereignAI.infrastructure.features", { returnObjects: true }) as Array<{ title: string; description: string }>);
  const sectors = (t("sovereignAI.sectors.items", { returnObjects: true }) as Array<{ title: string; description: string; useCase: string }>);
  const liabilityModels = (t("sovereignAI.liability.models", { returnObjects: true }) as Array<{ model: string; role: string; responsibility: string; benefit: string }>);
  const maturityLevels = (t("sovereignAI.maturity.levels", { returnObjects: true }) as Array<{ level: string; title: string; description: string; target: string }>);
  const ceeAdvantages = (t("sovereignAI.ceeAdvantage.items", { returnObjects: true }) as Array<{ title: string; description: string }>);
  const petFeatures = (t("sovereignAI.pets.features", { returnObjects: true }) as Array<{ title: string; description: string }>);

  return (
    <article>
      <ServicePageJsonLd name={t("sovereignAI.badge")} description={t("sovereignAI.subtitle")} path="/sovereign-ai" />
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-background">
          <div className="container py-20 md:py-28">
            <div className="mx-auto max-w-4xl text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-4 py-1.5 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                {t("sovereignAI.badge")}
              </div>
              <h1 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl">
                {t("sovereignAI.title")}
              </h1>
              <p className="mt-6 text-lg text-muted-foreground md:text-xl max-w-3xl mx-auto">
                {t("sovereignAI.subtitle")}
              </p>
              <p className="mt-4 text-base text-muted-foreground max-w-2xl mx-auto">
                {t("sovereignAI.description")}
              </p>
              <div className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
                <Button size="lg" className="w-full sm:w-auto" onClick={() => openContactForm({ page: "Sovereign AI", button: "Discuss Compliance Strategy", buttonPosition: "hero" })}>
                  {t("sovereignAI.cta.primary")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto" asChild>
                  <a href={getLocalizedPath("/services/consulting")}>
                    {t("sovereignAI.cta.secondary")}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* The Challenge Section */}
        <section className="py-20 bg-muted">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.challenge.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.challenge.description")}
              </p>
            </div>
            <div className="mx-auto max-w-4xl">
              <Card className="border-yellow-600/20 bg-yellow-50">
                <CardContent className="p-8">
                  <div className="flex gap-4">
                    <AlertTriangle className="h-8 w-8 shrink-0 text-yellow-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {t("sovereignAI.challenge.warning.title")}
                      </h3>
                      <p className="text-muted-foreground">
                        {t("sovereignAI.challenge.warning.description")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <div className="rounded-lg border border-border/50 bg-card/50 p-6">
                  <h4 className="font-semibold text-foreground mb-2">{t("sovereignAI.challenge.before.title")}</h4>
                  <p className="text-sm text-muted-foreground">{t("sovereignAI.challenge.before.description")}</p>
                </div>
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-6">
                  <h4 className="font-semibold text-foreground mb-2">{t("sovereignAI.challenge.after.title")}</h4>
                  <p className="text-sm text-muted-foreground">{t("sovereignAI.challenge.after.description")}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Regulatory Landscape Section */}
        <section className="py-20 bg-background">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.regulations.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.regulations.subtitle")}
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {regulations.map((reg, index) => {
                const Icon = regulationIcons[index] || Scale;
                return (
                  <div key={reg.title} className="flex gap-4 rounded-lg border border-border/50 bg-card/50 p-6">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{reg.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{reg.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Liability Configuration Section */}
        <section className="py-20 bg-muted">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.liability.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.liability.subtitle")}
              </p>
            </div>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {liabilityModels.map((model, index) => {
                const Icon = liabilityIcons[index] || Briefcase;
                return (
                  <Card key={model.model} className="border-border/50">
                    <CardContent className="pt-6">
                      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground">{model.model}</h3>
                      <div className="mt-4 space-y-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">{t("sovereignAI.liability.roleLabel")}:</span>
                          <span className="ml-2 text-foreground">{model.role}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{t("sovereignAI.liability.responsibilityLabel")}:</span>
                          <span className="ml-2 text-foreground">{model.responsibility}</span>
                        </div>
                        <div className="pt-2 border-t border-border/50">
                          <span className="text-primary font-medium">{model.benefit}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Sovereign Infrastructure Section */}
        <section className="py-20 bg-background">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.infrastructure.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.infrastructure.subtitle")}
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {infrastructureFeatures.map((feature, index) => {
                const Icon = infrastructureIcons[index] || Server;
                return (
                  <div key={feature.title} className="flex gap-4 rounded-lg border border-border/50 bg-card/50 p-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{feature.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Privacy-Enhancing Technologies Section */}
        <section className="py-20 bg-muted">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.pets.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.pets.subtitle")}
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {petFeatures.map((feature) => (
                <Card key={feature.title} className="border-border/50">
                  <CardContent className="pt-6">
                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Lock className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Sector Deep Dives Section */}
        <section className="py-20 bg-background">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.sectors.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.sectors.subtitle")}
              </p>
            </div>
            <div className="mt-12 grid gap-8 lg:grid-cols-3">
              {sectors.map((sector, index) => {
                const Icon = sectorIcons[index] || Building2;
                return (
                  <Card key={sector.title} className="border-border/50">
                    <CardContent className="pt-6">
                      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Icon className="h-7 w-7" />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground">{sector.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground">{sector.description}</p>
                      <div className="mt-4 pt-4 border-t border-border/50">
                        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                          {t("sovereignAI.sectors.useCaseLabel")}
                        </p>
                        <p className="text-sm text-foreground">{sector.useCase}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CEE Advantage Section */}
        <section className="py-20 bg-muted">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.ceeAdvantage.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.ceeAdvantage.subtitle")}
              </p>
            </div>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {ceeAdvantages.map((advantage, index) => {
                const Icon = advantageIcons[index] || MapPin;
                return (
                  <div key={advantage.title} className="flex gap-4 rounded-lg border border-border/50 bg-card/50 p-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{advantage.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{advantage.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Maturity Model Section */}
        <section className="py-20 bg-background">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.maturity.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.maturity.subtitle")}
              </p>
            </div>
            <div className="mt-12 grid gap-6 lg:grid-cols-3">
              {maturityLevels.map((level, index) => (
                <Card key={level.level} className={`border-border/50 ${index === 2 ? 'ring-2 ring-primary/20' : ''}`}>
                  <CardContent className="pt-6">
                    <div className="mb-4 flex items-center gap-3">
                      <span className="text-3xl font-bold text-primary/30">{level.level}</span>
                      <h3 className="text-lg font-semibold text-foreground">{level.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{level.description}</p>
                    <div className="mt-4 pt-4 border-t border-border/50">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
                        {t("sovereignAI.maturity.targetLabel")}
                      </p>
                      <p className="text-sm text-foreground">{level.target}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ISO 42001 Section */}
        <section className="py-20 bg-muted">
          <div className="container">
            <div className="mx-auto max-w-3xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold tracking-tight text-foreground">
                  {t("sovereignAI.iso.title")}
                </h2>
                <p className="mt-4 text-muted-foreground">
                  {t("sovereignAI.iso.subtitle")}
                </p>
              </div>
              <Card className="border-primary/20 bg-gradient-to-b from-primary/5 to-transparent">
                <CardContent className="p-8">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <h4 className="font-semibold text-foreground mb-4">{t("sovereignAI.iso.benefits.title")}</h4>
                      <ul className="space-y-3">
                        {(t("sovereignAI.iso.benefits.items", { returnObjects: true }) as string[]).map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-foreground">
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-primary mt-0.5" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex flex-col justify-center items-center bg-muted/30 rounded-lg p-6">
                      <p className="text-4xl font-bold text-primary">
                        {t("sovereignAI.iso.statValue")}
                      </p>
                      <p className="text-sm text-muted-foreground text-center mt-2">
                        {t("sovereignAI.iso.stat")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-background">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-foreground">
                {t("sovereignAI.finalCta.title")}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t("sovereignAI.finalCta.description")}
              </p>
              <div className="mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-center sm:gap-4">
                <Button size="lg" className="w-full sm:w-auto" onClick={() => openContactForm({ page: "Sovereign AI", button: "Final CTA", buttonPosition: "bottom" })}>
                  {t("sovereignAI.finalCta.button")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <p className="mt-6 text-sm text-muted-foreground">
                {t("sovereignAI.finalCta.note")}
              </p>
            </div>
          </div>
        </section>
    </article>
  );
}
